/**
 * Schedule Follow-up Modal
 * 
 * Allows scheduling a follow-up with date/time picker and automatic reminders
 * (1 day before, 2 hours before)
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { EventsService } from '@/data/events.service';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { validateWorkspaceForCreate } from '@/lib/workspaceValidation';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

interface ScheduleFollowUpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadName: string;
}

export function ScheduleFollowUpModal({
  open,
  onOpenChange,
  leadId,
  leadName,
}: ScheduleFollowUpModalProps) {
  const { activeWorkspaceId, mode } = useWorkspaceScope();
  const queryClient = useQueryClient();
  
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSchedule = async () => {
    if (!selectedDate) return;

    // Validate workspace before scheduling
    const validation = validateWorkspaceForCreate(activeWorkspaceId, mode, 'schedule a follow-up');
    if (!validation.isValid) {
      return; // Toast already shown by validateWorkspaceForCreate
    }

    const workspaceId = validation.workspaceId!;

    setIsSubmitting(true);
    try {
      // Parse time
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const scheduledDate = new Date(selectedDate);
      scheduledDate.setHours(hours, minutes, 0, 0);

      // Ensure date is in the future
      if (scheduledDate < new Date()) {
        toast.error('Please select a future date and time');
        setIsSubmitting(false);
        return;
      }

      // Update lead with next_follow_up_at directly
      const { LeadsService } = await import('@/data/leads.service');
      await LeadsService.updateLead({
        id: leadId,
        workspace_id: workspaceId,
        next_follow_up_at: scheduledDate.toISOString(),
      });

      // Create calendar event with reminders: 1 day before (1440 min) and 2 hours before (120 min)
      const endDate = new Date(scheduledDate);
      endDate.setHours(scheduledDate.getHours() + 1); // 1 hour duration

      await EventsService.createEvent({
        workspace_id: workspaceId,
        title: `Follow up: ${leadName}`,
        type: 'meeting',
        start_at: scheduledDate.toISOString(),
        end_at: endDate.toISOString(),
        lead_id: leadId,
        priority: 'important', // Important to get the reminders
        reminder_offsets_minutes: [1440, 120], // 1 day before, 2 hours before
      });

      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['reminder-events', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['elite-today-plan', workspaceId] });

      toast.success('Follow-up scheduled', {
        description: `Reminders set for 1 day before and 2 hours before.`,
      });

      onOpenChange(false);
      
      // Reset form
      setSelectedDate(new Date());
      setSelectedTime('09:00');
    } catch (error: any) {
      logger.error('[ScheduleFollowUpModal] Failed to schedule follow-up', error, {
        leadId,
        workspaceId,
      });
      toast.error('Failed to schedule follow-up');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Schedule Follow-up</DialogTitle>
          <DialogDescription>
            Set a date and time for following up with {leadName}. You'll be reminded 1 day before and 2 hours before.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Date Picker */}
          <div className="space-y-2">
            <Label>Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Picker */}
          <div className="space-y-2">
            <Label>Time</Label>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSchedule}
            disabled={!selectedDate || isSubmitting}
          >
            {isSubmitting ? 'Scheduling...' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

